// ============================================================
// TERROR ENGINE — PORTRAIT FRAME  (split-screen shell)
// ------------------------------------------------------------
// The tall-mode layout the Illegal Empire runs on. In portrait on a
// phone the screen is split into two stacked zones:
//
//   ┌───────────────────────────┐
//   │                           │
//   │        WORLD ZONE         │  ← gameplay / plaza / room renders here
//   │     (top, ~58% tall)      │     camera-follow world lives in here
//   │                           │
//   ├───────────────────────────┤
//   │                           │
//   │        DOCK ZONE          │  ← d-pad + every popup lands here
//   │    (bottom, ~42% tall)    │     pet close-up, prompts, toasts…
//   │                           │
//   └───────────────────────────┘
//
// On desktop / landscape / non-portrait this is a NO-OP pass-through:
// children render exactly as before, full-bleed. So a screen can wrap
// itself in <PortraitFrame> unconditionally and only pay for it on a
// phone held upright.
//
// ── HOW POPUPS REACH THE DOCK ───────────────────────────────
// PortraitFrame mounts a DOM node for the dock and shares it via
// context. Any descendant renders into it with <DockPortal>:
//
//   import { PortraitFrame, DockPortal } from "./engineIndex";
//
//   <PortraitFrame world={<TheWorld />}>
//     <MobileDpad keysRef={keysRef} />     // lives in the dock
//     {showPet && (
//       <DockPortal>                       // teleports into dock empty space
//         <CompanionCloseup ... />
//       </DockPortal>
//     )}
//   </PortraitFrame>
//
// DockPortal, when NOT in portrait (or used outside a frame), simply
// renders its children inline — so the same JSX works on desktop where
// the popup should just be a normal centered overlay.
//
// The hook `useDock()` returns { inPortrait, dockEl, dockHeight } for
// components that want to make their own placement decisions.
// ============================================================

import { createContext, useContext, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { usePortrait } from "./OrientationLock";

// Fraction of viewport height given to the WORLD zone in portrait.
// The remainder is the dock. Tuned so the world still reads big while
// leaving a genuine half-ish for the d-pad + popups.
const WORLD_FRACTION = 0.62;

const DockCtx = createContext({ inPortrait: false, dockEl: null, dockHeight: 0 });

export function useDock() {
  return useContext(DockCtx);
}

// ── PortraitFrame ─────────────────────────────────────────────
// Props:
//   world     ReactNode  — the gameplay surface (camera world, room…).
//                          Rendered into the TOP zone in portrait.
//   children  ReactNode  — chrome that should live in the DOCK zone
//                          (d-pad, menu button, DockPortal popups).
//   worldStyle / dockStyle  — optional style overrides per zone.
//   bg        string     — backdrop colour behind both zones.
//
// In non-portrait it renders <>{world}{children}</> untouched.
export function PortraitFrame({
  world,
  children,
  worldStyle = {},
  dockStyle = {},
  bg = "#050308",
}) {
  const inPortrait = usePortrait();
  const dockRef = useRef(null);
  // Force a re-render once the dock node mounts so DockPortal children
  // (which need dockRef.current) can attach on the same paint.
  const [, setReady] = useState(0);
  useEffect(() => { setReady(n => n + 1); }, [inPortrait]);

  const [vh, setVh] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 800);
  useEffect(() => {
    if (!inPortrait) return;
    const update = () => setVh(window.innerHeight);
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [inPortrait]);

  // ── Desktop / landscape: pass straight through. ──────────────
  if (!inPortrait) {
    return (
      <DockCtx.Provider value={{ inPortrait: false, dockEl: null, dockHeight: 0 }}>
        {world}
        {children}
      </DockCtx.Provider>
    );
  }

  const worldH = Math.round(vh * WORLD_FRACTION);
  const dockH  = vh - worldH;

  return (
    <DockCtx.Provider value={{ inPortrait: true, dockEl: dockRef.current, dockHeight: dockH }}>
      <div style={{
        position: "fixed", inset: 0, background: bg,
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        {/* ── WORLD ZONE (top) ───────────────────────────────── */}
        <div style={{
          position: "relative",
          height: worldH,
          width: "100%",
          overflow: "hidden",
          flexShrink: 0,
          ...worldStyle,
        }}>
          {world}
          {/* soft seam so the world fades into the dock */}
          <div style={{
            position: "absolute", left: 0, right: 0, bottom: 0, height: 18,
            pointerEvents: "none",
            background: `linear-gradient(180deg, transparent, ${bg})`,
            zIndex: 5,
          }} />
        </div>

        {/* ── DOCK ZONE (bottom) ─────────────────────────────── */}
        <div
          ref={dockRef}
          style={{
            position: "relative",
            height: dockH,
            width: "100%",
            flexShrink: 0,
            overflow: "hidden",
            background: `linear-gradient(180deg, ${bg} 0%, #07050f 100%)`,
            borderTop: "1px solid rgba(200,150,60,0.18)",
            boxShadow: "inset 0 14px 28px rgba(0,0,0,0.55)",
            ...dockStyle,
          }}
        >
          {/* faint manuscript ruling so the empty dock isn't dead space */}
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.5,
            background:
              "repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(139,106,48,0.07) 28px)",
          }} />
          {children}
        </div>
      </div>
    </DockCtx.Provider>
  );
}

// ── DockPortal ────────────────────────────────────────────────
// Renders children into the dock zone when in portrait, otherwise
// renders them inline (normal overlay). Use for any popup that should
// occupy the reserved bottom space on a phone — petting, prompts, etc.
//
// Works in two modes:
//   • inside <PortraitFrame> → uses the frame's dock via context.
//   • standalone → set `targetId` (default "ie-dock") to teleport into
//     a dock element a screen mounts itself (e.g. Plaza's manual dock).
//     Falls back to inline render if that element isn't present or we're
//     not in portrait.
//
// Props:
//   children  ReactNode
//   center    boolean   — center content within the dock (default true)
//   targetId  string    — DOM id of a standalone dock element
export function DockPortal({ children, center = true, align = "center", targetId = "ie-dock" }) {
  const { inPortrait: ctxPortrait, dockEl: ctxDock } = useDock();
  const livePortrait = usePortrait();
  // Re-render once after mount so a standalone dock element rendered in
  // the same commit is found on the second pass (it may not be attached
  // yet on the first).
  const [, bump] = useState(0);
  useEffect(() => { bump(n => n + 1); }, [livePortrait, ctxPortrait]);

  // Resolve the destination node: context frame first, then a standalone
  // dock element by id.
  let dest = ctxPortrait ? ctxDock : null;
  if (!dest && livePortrait && typeof document !== "undefined") {
    dest = document.getElementById(targetId);
  }

  if (!dest) {
    // Desktop / landscape / no dock available → render inline.
    return children;
  }

  const justify = align === "top" ? "flex-start"
                : align === "bottom" ? "flex-end"
                : "center";

  const wrapped = center ? (
    <div style={{
      position: "absolute", inset: 0,
      display: "flex", alignItems: justify, justifyContent: "center",
      padding: align === "top" ? "14px 10px 10px" : 10,
      boxSizing: "border-box",
    }}>
      {children}
    </div>
  ) : children;

  return createPortal(wrapped, dest);
}

export default PortraitFrame;
